<template>
  <v-container fluid>
    <client-action-dialog v-if="clientActionDialogModel" v-model="clientActionDialogModel"/>
    <v-row dense no-gutters>
      <v-col
      class="pa-4">
        <p class="process-repair-tab-section-title">{{$t("pages.activeRepairs.title")}}</p>
        <main-data-table
        ref="tableRef"
        :headers="getHeaders"
        :items="items"
        number-pages
        :page.sync="page"
        :size.sync="size"
        :keyword.sync="keyword"
        @fetch="tryToFetchRepairs"
        :sort-by.sync="sortBy"
        :sort-dir.sync="sortDir"
        :total-elements="totalElements"
        :loading="isLoading">
          <template v-slot:[`header.requestId`]="{ header }">
            {{$t(header.text)}}
          </template>
          <template v-slot:[`header.status`]="{ header }">
            {{$t(header.text)}}
          </template>
          <template v-slot:[`header.actionRequired`]="{ header }">
            {{$t(header.text)}}
          </template>
          <template v-slot:[`header.createdAt`]="{ header }">
            {{$t(header.text, {'0': getTimeZoneInNumber(new Date())})}}
          </template>
          <template v-slot:[`header.updatedAt`]="{ header }">
            {{$t(header.text, {'0': getTimeZoneInNumber(new Date())})}}
          </template>
          <template v-slot:[`header.details`]="{ header }">
            {{$t(header.text)}}
          </template>
          
          <template v-slot:[`item.status`]="{ item }">
            <span>
              {{$t(getStatus(item.status))}}
            </span>
          </template>
          <template v-slot:[`item.actionRequired`]="{ item }">
            <span>
              {{$t('other.from')}} {{item.actionRequired.toLowerCase()}}
            </span>
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
            <span>
              {{getDateNewYorkFormat(item.createdAt)}}
            </span>
          </template>
          <template v-slot:[`item.updatedAt`]="{ item }">
            <span>
              {{getDateNewYorkFormat(item.updatedAt)}}
            </span>
          </template>
          <template v-slot:[`item.details`]="{ item }">
            <route-btn
            :href="`process-repair/${item.id}`"
            label="btns.viewDetails"
            :badge="item.actionRequiredFromCurrentUser ? 1 : null"
            :width="120"
            :height="30"/>
          </template>
        </main-data-table>
      </v-col>
    </v-row>

    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="items.length === 0"
    :loading.sync="isLoading"
    class="px-4"
    name="export.title.activeRepairs"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import { fetchActiveRepairs } from '@/repositories/repairRepo'
import { execHttpRequestThrowable } from '@/services/http'
import { getRepairStatusByKey } from '@/utils/statusesUtil'
import DateUtil from '@/utils/DateUtil'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import { routeFromRoot } from '@/services/routeService'
import { isLanguageEn, getUserRole } from '@/services/authService'
import { exportActiveRepairsXlsx } from '@/repositories/exportRepo'
import MainDataTable from '@/components/common/MainDataTable'
import ClientActionDialog from '@/components/dialogs/ClientActionDialog'
import RouteBtn from '@/components/btns/RouteBtn'

export default {
  components: {
    MainDataTable,
    ClientActionDialog,
    ExportXlsxRow,
    RouteBtn
  },
  data() {
    return {
      headers: [
        {
          text: 'headers.requestId',
          align: 'start',
          sortable: true,
          value: 'requestId',
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.clientName',
          align: 'start',
          sortable: true,
          value: 'clientUsername',
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.status',
          align: 'start',
          sortable: true,
          value: 'status',
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.actionRequired',
          align: 'start',
          sortable: true,
          value: 'actionRequired',
          columnName: "actionRequiredFromRole",
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.creationDateTime',
          align: 'start',
          sortable: true,
          value: 'createdAt', //(yyyy-MM-dd HH:mm)
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.lastChangeDateTime',
          align: 'start',
          sortable: true,
          value: 'updatedAt', //(yyyy-MM-dd HH:mm)
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.details',
          align: 'start',
          sortable: false,
          value: 'details',
          permissions: ["ADMIN", "SUPER_ADMIN", "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }
      ],
      sortBy: "createdAt",
      sortDir: "DESC",
      keyword: "",
      items: [],
      totalElements: 0,
      size: 10,
      page: 1,
      isLoading: false,
      clientActionDialogModel: false
    }
  },
  computed: {
    isEN() {
      return isLanguageEn()
    },
    getRole() {
      return getUserRole()
    },
    getExporter() {
      return async () => await exportActiveRepairsXlsx(this.keyword, this.getSort)
    },
    getHeaders() {
      return this.headers.filter(o => o.permissions.includes(this.getRole))
    },
    getSort() {
      return `${this.$refs.tableRef.getColumnNameByHeaderValue(this.sortBy)},${this.sortDir}`
    }
  },
  methods: {
    async tryToFetchRepairs({search, size, page, sort}) {
      this.items = []
      const r = await execHttpRequestThrowable(async () => await fetchActiveRepairs(search, size, page - 1, sort), {}, v => this.isLoading = v)
      this.pageable = r.response.data
      this.totalElements = this.pageable.totalElements
      this.items = this.pageable.content
    },
    getDateNewYorkFormat(d) {
      return DateUtil.getDateNewYorkFormat(d)
    },
    getStatus(k) {
      return getRepairStatusByKey(k)
    },
    routeTo(link) {
      routeFromRoot(link)
    },
    getTimeZoneInNumber(d) {
      return DateUtil.getTimeZoneInNumber(d)
    }
  },
  async mounted() {
    await this.tryToFetchRepairs({
      search: this.keyword,
      size: this.size,
      page: this.page,
      sort: this.getSort
    })
  },
  created() {
    if (this.$route.hash === '#suggest-options' && this.getRole === 'CLIENT') {
      this.clientActionDialogModel = true
    }

    this.$feedback.fill("Active Repairs Page", this.$route.path)
  }
}
</script>
<style>
.detail-btn.v-btn.v-btn--is-elevated.v-btn--has-bg.theme--light {
  color: #fff;
  background-color: #4285f4!important;
  border-color: #75a4f0;
  font-family: 'Titillium Web', sans-serif;
  font-weight: 400;
  font-size: 0.65rem;
  letter-spacing: 1px;
}
</style>